import { RobotOutlined } from "@ant-design/icons";
import { useUser } from "@p0security/frontend-core";
import type {
  AgenticClientListItem,
  AgenticClientUpdateRequest,
  AgenticFederatedClient,
} from "@p0security/integrations-agentic-shared/interface";
import { isAgenticClientDisabled } from "@p0security/integrations-agentic-shared/util";
import { itemRouteSegment } from "@p0security/shared/install/routes";
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Flex,
  Switch,
  Tooltip,
  Typography,
} from "@p0security/ui";
import { DetailsDescriptions } from "components/Jit/DetailsDescriptions";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { get } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";

import { OauthCredentials } from "./OauthCredentials";
import { OauthRegistrationModal } from "./OauthRegistrationModal";
import {
  formatMcpClientName,
  formatMcpClientPlatform,
  mcpClientPlatformIcon,
} from "./clientPlatforms";

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

type DetailItemOptions = {
  copyable?: boolean;
  monospace?: boolean;
};

const renderDetailItem = (
  label: string,
  value?: string,
  { copyable, monospace }: DetailItemOptions = {}
) => (
  <Descriptions.Item label={label}>
    {value ? (
      <Typography.Text
        className={monospace ? "break-all font-mono text-xs" : undefined}
        copyable={copyable ? { text: value } : undefined}
      >
        {value}
      </Typography.Text>
    ) : (
      <Typography.Text type="secondary">—</Typography.Text>
    )}
  </Descriptions.Item>
);

const registrationSource = (client: AgenticClientListItem) =>
  client.type === "jwt_bearer"
    ? get(client.createdBy, "gateway")
    : client.createdBy?.user;

type RegistrableClient = Pick<
  AgenticFederatedClient,
  "id" | "owner" | "status"
>;

/** Why `userEmail` can't register an OAuth credential for `client`, or
 * `undefined` if they can. */
const registrationBlocker = (
  client: RegistrableClient,
  userEmail: string | null | undefined
) =>
  // Mirrors the gateway, which rejects a disabled client and anyone but the
  // client's owner, so the operator learns why before leaving P0.
  isAgenticClientDisabled(client)
    ? "Activate this client to register an OAuth credential."
    : !client.owner
      ? "Set an owner on this client to register an OAuth credential."
      : client.owner.toLowerCase() !== userEmail?.toLowerCase()
        ? `Only the client's owner, ${client.owner}, can register an OAuth credential.`
        : undefined;

/** Opens the gateway flow that binds an MCP server's OAuth credential to a
 * federated client. */
const RegisterOauthCredential: React.FC<{
  canManageServers: boolean;
  client: RegistrableClient;
}> = ({ canManageServers, client }) => {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);
  const blocker = registrationBlocker(client, user?.email);

  return (
    <>
      <Tooltip title={blocker}>
        {/* A disabled button fires no mouse events, so the wrapper is what
            opens the tooltip explaining why it's disabled. */}
        <span>
          <Button
            aria-label="Register OAuth credential"
            disabled={blocker !== undefined}
            type="primary"
            onClick={openModal}
          >
            Register
          </Button>
        </span>
      </Tooltip>
      <OauthRegistrationModal
        canManageServers={canManageServers}
        clientId={client.id}
        open={open}
        onClose={closeModal}
      />
    </>
  );
};

type EditableTextProps = {
  editable: boolean;
  /** Shown in place of a missing value. */
  emptyText?: string;
  /** Field label; names the edit button for assistive technology. */
  label: string;
  maxLength: number;
  value?: string;
  /** Saves the trimmed draft, or null when it's blank. */
  onSave: (value: string | null) => unknown;
};

/** A client detail that client managers edit in place. */
const EditableText: React.FC<EditableTextProps> = (props) => {
  const { editable, label, maxLength, onSave, value } = props;
  const emptyText = props.emptyText ?? "—";

  const save = useCallback(
    (draft: string) => {
      const next = draft.trim() || null;
      // antd also confirms on blur, so skip saves that change nothing.
      if (next !== (value ?? null)) onSave(next);
    },
    [onSave, value]
  );

  return (
    <Typography.Text
      editable={
        editable && {
          maxLength,
          text: value ?? "",
          tooltip: `Edit ${label.toLowerCase()}`,
          onChange: save,
        }
      }
      type={value ? undefined : "secondary"}
    >
      {value || emptyText}
    </Typography.Text>
  );
};

type ClientDetailsDrawerProps = {
  canManageServers: boolean;
  canRegisterOauth: boolean;
  client?: AgenticClientListItem;
  error?: string;
  loading: boolean;
  onClose: () => void;
  onUpdate: (
    clientId: string,
    metadata: AgenticClientUpdateRequest
  ) => Promise<AgenticClientListItem | undefined>;
};

const ClientDetails: React.FC<
  Omit<ClientDetailsDrawerProps, "client" | "onClose"> & {
    client: AgenticClientListItem;
  }
> = (props) => {
  const { canManageServers, canRegisterOauth } = props;
  const { client: providedClient, error, loading, onUpdate } = props;
  const [client, setClient] = useState(providedClient);
  const updateInFlight = useRef(false);

  useEffect(() => setClient(providedClient), [providedClient]);

  const updateClient = useCallback(
    async (metadata: AgenticClientUpdateRequest) => {
      if (loading || updateInFlight.current) return;
      updateInFlight.current = true;
      try {
        const updated = await onUpdate(client.id, metadata);
        if (updated) setClient(updated);
        return updated;
      } finally {
        updateInFlight.current = false;
      }
    },
    [client.id, loading, onUpdate]
  );

  const saveDisplayName = useCallback(
    (displayName: string | null) => updateClient({ displayName }),
    [updateClient]
  );
  const saveOwner = useCallback(
    (owner: string | null) => updateClient({ owner }),
    [updateClient]
  );

  const toggleEnabled = useCallback(
    (enabled: boolean) =>
      void updateClient({ status: enabled ? "active" : "disabled" }),
    [updateClient]
  );

  return (
    <Flex gap={20} vertical>
      <Flex align="center" gap={12}>
        <RobotOutlined
          className="text-xl text-[var(--ant-color-text-secondary)]"
          aria-hidden="true"
        />
        <Flex gap={2} vertical>
          <Typography.Title className="!m-0" level={4}>
            {formatMcpClientName(client)}
          </Typography.Title>
          <Typography.Text type="secondary">Registered client</Typography.Text>
        </Flex>
      </Flex>

      {error && <Alert message={error} showIcon type="error" />}
      <DetailsDescriptions>
        <Descriptions.Item label="Display name">
          <EditableText
            editable={canManageServers}
            label="Display name"
            maxLength={100}
            value={client.displayName}
            onSave={saveDisplayName}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Owner">
          <EditableText
            editable={canManageServers}
            emptyText="No owner"
            label="Owner"
            maxLength={320}
            value={client.owner}
            onSave={saveOwner}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Flex align="center" gap={8}>
            <Switch
              aria-label="Client enabled"
              checked={client.status !== "disabled"}
              disabled={!canManageServers || loading}
              loading={loading}
              size="small"
              onChange={toggleEnabled}
            />
            <Typography.Text>
              {client.status === "disabled" ? "Disabled" : "Active"}
            </Typography.Text>
          </Flex>
        </Descriptions.Item>
        {renderDetailItem("Client ID", client.id, {
          copyable: true,
          monospace: true,
        })}
        <Descriptions.Item label="Authentication">
          {client.type === "jwt_bearer" ? "Federated" : "Client credentials"}
        </Descriptions.Item>
        {client.type === "jwt_bearer" ? (
          <>
            <Descriptions.Item label="Identity provider">
              <TenantAwareLink
                to={`integrations/identity-provider/provider/${itemRouteSegment(
                  client.providerId
                )}`}
              >
                {client.providerId}
              </TenantAwareLink>
            </Descriptions.Item>
            {renderDetailItem("Issuer", client.issuer, {
              monospace: true,
            })}
          </>
        ) : (
          <>
            <Descriptions.Item label="Platform">
              <Flex align="center" gap={8}>
                {mcpClientPlatformIcon(client.platform)}
                <span>{formatMcpClientPlatform(client.platform)}</span>
              </Flex>
            </Descriptions.Item>
            {renderDetailItem("Version", client.version, {
              monospace: true,
            })}
            {renderDetailItem("Hostname", client.hostname)}
            {renderDetailItem("Redirect URI", client.redirectUri, {
              copyable: true,
              monospace: true,
            })}
          </>
        )}
        {renderDetailItem("Registered by", registrationSource(client))}
        <Descriptions.Item label="Registered at">
          {client.createdAt === undefined ? (
            <Typography.Text type="secondary">Not recorded</Typography.Text>
          ) : (
            <time dateTime={new Date(client.createdAt).toISOString()}>
              {DATE_FORMAT.format(new Date(client.createdAt))}
            </time>
          )}
        </Descriptions.Item>
      </DetailsDescriptions>
      {(canRegisterOauth || canManageServers) &&
        client.type === "jwt_bearer" && (
          <Flex gap={12} vertical>
            <Flex align="center" justify="space-between">
              <Typography.Title className="!m-0" level={5}>
                OAuth credentials
              </Typography.Title>
              {canRegisterOauth && (
                <RegisterOauthCredential
                  canManageServers={canManageServers}
                  client={client}
                />
              )}
            </Flex>
            {canManageServers && <OauthCredentials clientId={client.id} />}
          </Flex>
        )}
    </Flex>
  );
};

/** Displays and edits registration and authentication details for a client. */
export const ClientDetailsDrawer: React.FC<ClientDetailsDrawerProps> = (
  props
) => {
  const { client, loading, onClose } = props;
  return (
    <Drawer
      closable={!loading}
      destroyOnHidden
      keyboard={!loading}
      maskClosable={!loading}
      open={Boolean(client)}
      title="Client details"
      width="75%"
      onClose={onClose}
    >
      {client && <ClientDetails key={client.id} {...props} client={client} />}
    </Drawer>
  );
};
